/*
 * lib/auth.js
 *
 * Username + password auth bovenop Supabase Auth.
 *
 * Supabase Auth zelf kent geen "username" — alleen email. We laten dat
 * onveranderd en geven Supabase intern een "fake email":
 *
 *   gebruiker tikt "Sven"     →     auth.users.email = "sven@florijn.invalid"
 *
 * Het ".invalid"-TLD is door RFC 2606 gereserveerd voor exact dit soort
 * niet-bezorgbare adressen, dus niemand kan er ooit per ongeluk naartoe
 * mailen. De gebruiker ziet die fake email nooit.
 *
 * Mappings:
 *   - signup  → supabase.auth.signUp({ email, password, options.data })
 *               waarbij options.data = { username, display_name }; de
 *               handle_new_user-trigger leest die en zet ze op profiles.
 *   - signin  → supabase.auth.signInWithPassword({ email, password })
 *   - check   → RPC `is_username_taken(text)` — zonder profile-table
 *               te exposen aan anon-role.
 */

import { supabase, requireSupabase } from './supabase.js';
import { clearAllCaches } from './cache.js';

const FAKE_DOMAIN = 'florijn.invalid';

/** Bouwe de fake-email die we naar Supabase Auth sturen. */
function usernameToFakeEmail(username) {
  return `${String(username).trim().toLowerCase()}@${FAKE_DOMAIN}`;
}

/** Is een username al in gebruik? Roept de public RPC aan (= geen direct
 *  inkijk in de profiles-tabel nodig vanuit anon). */
export async function isUsernameTaken(username) {
  const sb = requireSupabase();
  const { data, error } = await sb.rpc('is_username_taken', {
    input_username: String(username).trim()
  });
  if (error) throw error;
  return !!data;
}

/** Maak een nieuw account met username + wachtwoord. */
export async function signUpWithUsername(username, password) {
  const sb = requireSupabase();
  const clean = String(username).trim();
  const { data, error } = await sb.auth.signUp({
    email: usernameToFakeEmail(clean),
    password,
    options: {
      // De handle_new_user-trigger leest deze metadata uit en zet ze op
      // het profile. Zo behouden we de hoofdletter zoals de gebruiker tikte.
      data: { username: clean, display_name: clean }
    }
  });
  if (error) throw error;
  return data;
}

/** Log in met username + wachtwoord. */
export async function signInWithUsername(username, password) {
  const sb = requireSupabase();
  const { data, error } = await sb.auth.signInWithPassword({
    email: usernameToFakeEmail(username),
    password
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const sb = requireSupabase();
  // Wis local-first caches voor we uitloggen — anders zou een volgende user
  // op hetzelfde toestel ze kunnen tegenkomen als hij ooit dezelfde user-id
  // had (zeer zeldzaam maar veiliger).
  try { clearAllCaches(); } catch {}
  await sb.auth.signOut();
  // Force a clean reload — clears any cached state in modules.
  window.location.reload();
}

/** Subscribe to auth state changes. Returns an unsubscribe function. */
export function onAuthChange(callback) {
  if (!supabase) {
    callback(null);
    return () => {};
  }
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session);
  });
  return () => data.subscription.unsubscribe();
}

/** Get the current session, or null. */
export async function getSession() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * Mount het login-scherm in #app met twee tabs:
 *   - Inloggen   → username + password → signInWithUsername
 *   - Aanmelden  → username + password (2×) → check beschikbaarheid → signUpWithUsername
 *
 * Roept `onSignedIn(session)` zodra er een geldige sessie is.
 */
export function mountAuthGate(onSignedIn) {
  const app = document.getElementById('app');
  let view = 'signin';   // 'signin' | 'signup'

  // Bewaar getypte waarden bij tab-switch zodat de gebruiker niet
  // opnieuw moet beginnen. Wachtwoord wissen we wel — veiliger.
  let cachedUsername = '';

  function render() {
    app.innerHTML = `
      <div class="auth-gate">
        <div class="auth-card">
          <img src="/favicon.svg" alt="Florijn-logo" class="auth-logo" width="72" height="72">
          <h1 class="auth-title">Florijn</h1>

          <div class="auth-tabs" role="tablist">
            <button type="button" role="tab" class="auth-tab${view === 'signin' ? ' active' : ''}" data-view="signin">Inloggen</button>
            <button type="button" role="tab" class="auth-tab${view === 'signup' ? ' active' : ''}" data-view="signup">Aanmelden</button>
          </div>

          ${view === 'signin' ? renderSignInView() : renderSignUpView()}
        </div>
      </div>`;
    bindHandlers();
  }

  function renderSignInView() {
    return `
      <form id="auth-form" class="auth-form" novalidate>
        <label for="auth-username" class="auth-label">Username</label>
        <input id="auth-username" type="text" class="auth-input" required
               autocomplete="username" pattern="^[A-Za-z0-9_-]{3,32}$"
               placeholder="Sven" value="${escapeAttr(cachedUsername)}">

        <label for="auth-password" class="auth-label">Wachtwoord</label>
        <input id="auth-password" type="password" class="auth-input" required
               autocomplete="current-password" placeholder="••••••••">

        <button type="submit" class="auth-btn">Inloggen</button>
      </form>
      <p id="auth-msg" class="auth-msg" role="status" aria-live="polite"></p>`;
  }

  function renderSignUpView() {
    return `
      <form id="auth-form" class="auth-form" novalidate>
        <label for="auth-username" class="auth-label">Kies een username</label>
        <input id="auth-username" type="text" class="auth-input" required
               autocomplete="username" pattern="^[A-Za-z0-9_-]{3,32}$"
               placeholder="Sven" value="${escapeAttr(cachedUsername)}">
        <p class="auth-hint">3–32 tekens, letters/cijfers/_/-. Hoofdletters maken niet uit voor het inloggen.</p>

        <label for="auth-password" class="auth-label">Wachtwoord</label>
        <input id="auth-password" type="password" class="auth-input" required
               autocomplete="new-password" placeholder="••••••••" minlength="6">
        <p class="auth-hint">Minstens 6 tekens. Onthoud 'm goed — er is nog geen reset-flow.</p>

        <label for="auth-password2" class="auth-label">Wachtwoord bevestigen</label>
        <input id="auth-password2" type="password" class="auth-input" required
               autocomplete="new-password" placeholder="••••••••">

        <button type="submit" class="auth-btn">Account aanmaken</button>
      </form>
      <p id="auth-msg" class="auth-msg" role="status" aria-live="polite"></p>`;
  }

  function bindHandlers() {
    document.querySelectorAll('.auth-tab').forEach(t => {
      t.addEventListener('click', () => {
        // Onthoud wat er getypt was zodat de tab-switch niet leegslaat.
        const u = document.getElementById('auth-username');
        if (u) cachedUsername = u.value;
        view = t.dataset.view;
        render();
      });
    });

    const form = document.getElementById('auth-form');
    const usernameInput = document.getElementById('auth-username');
    const passwordInput = document.getElementById('auth-password');
    const msg = document.getElementById('auth-msg');
    usernameInput.focus();

    const showError = (text) => {
      msg.textContent = text;
      msg.className = 'auth-msg auth-msg-error';
    };

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = form.querySelector('button[type="submit"]');
      const username = usernameInput.value.trim();
      const password = passwordInput.value;

      if (!/^[A-Za-z0-9_-]{3,32}$/.test(username)) {
        showError('Username: 3–32 tekens, alleen letters, cijfers, _ of -.');
        usernameInput.focus();
        return;
      }
      if (password.length < 6) {
        showError('Wachtwoord moet minstens 6 tekens lang zijn.');
        passwordInput.focus();
        return;
      }

      msg.textContent = '';
      msg.className = 'auth-msg';

      if (view === 'signup') {
        const password2 = document.getElementById('auth-password2').value;
        if (password !== password2) {
          showError('De twee wachtwoorden komen niet overeen.');
          return;
        }

        submitBtn.disabled = true;
        submitBtn.textContent = 'Beschikbaarheid checken…';
        try {
          const taken = await isUsernameTaken(username);
          if (taken) {
            showError(`"${username}" is al in gebruik — kies iets anders.`);
            submitBtn.disabled = false;
            submitBtn.textContent = 'Account aanmaken';
            usernameInput.focus();
            usernameInput.select();
            return;
          }
        } catch (err) {
          showError('Beschikbaarheidscheck mislukt: ' + (err?.message || 'onbekende fout'));
          submitBtn.disabled = false;
          submitBtn.textContent = 'Account aanmaken';
          return;
        }

        submitBtn.textContent = 'Account aanmaken…';
        try {
          const { session } = await signUpWithUsername(username, password);
          if (!session) {
            // Komt voor wanneer "Confirm email" toch nog aan staat in Supabase.
            showError('Account aangemaakt, maar Supabase wil mail-bevestiging — check je config.');
            submitBtn.disabled = false;
            submitBtn.textContent = 'Account aanmaken';
            return;
          }
          unsub();
          onSignedIn(session);
        } catch (err) {
          const friendly = friendlySignupError(err);
          showError(friendly);
          submitBtn.disabled = false;
          submitBtn.textContent = 'Account aanmaken';
        }
        return;
      }

      // Sign in
      submitBtn.disabled = true;
      submitBtn.textContent = 'Inloggen…';
      try {
        const { session } = await signInWithUsername(username, password);
        unsub();
        onSignedIn(session);
      } catch (err) {
        const friendly = friendlySignInError(err);
        showError(friendly);
        submitBtn.disabled = false;
        submitBtn.textContent = 'Inloggen';
        passwordInput.focus();
        passwordInput.select();
      }
    });
  }

  // Fallback: als er ergens al een sessie binnenrolt (browser tab-sync,
  // andere flow), spring direct door.
  const unsub = onAuthChange(session => {
    if (session) {
      unsub();
      onSignedIn(session);
    }
  });

  render();
}

// ── helpers ──────────────────────────────────────

function friendlySignInError(err) {
  const m = (err?.message || '').toLowerCase();
  if (m.includes('invalid login') || m.includes('invalid credentials')) {
    return 'Username of wachtwoord klopt niet. Probeer opnieuw, of registreer een nieuw account.';
  }
  if (m.includes('email not confirmed')) {
    return 'Account staat als "onbevestigd" geregistreerd. Zet "Confirm email" uit in Supabase-instellingen.';
  }
  return 'Inloggen mislukt: ' + (err?.message || 'onbekende fout');
}

function friendlySignupError(err) {
  const m = (err?.message || '').toLowerCase();
  if (m.includes('already registered') || m.includes('user already')) {
    return 'Deze username bestaat al in Supabase. Kies een andere of log gewoon in.';
  }
  if (m.includes('weak password') || m.includes('password should')) {
    return 'Wachtwoord is te zwak. Probeer iets langer of complexer.';
  }
  return 'Account aanmaken mislukt: ' + (err?.message || 'onbekende fout');
}

function escapeAttr(str) {
  return String(str || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}
